var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, forwardRef, useContext, useImperativeHandle } from 'react';
import { Animated, Pressable, View } from 'react-native';
import { BLACK_500, BLACK_900, RED_500 } from '../../constants';
import { StateConfigContext } from '../../providers';
import { getTestId, isLastItem } from '../../utils';
import { InView } from '../in-view';
import { Typography } from '../typography';
import { getAnimatedCarouselContainerStyle, getAnimatedCarouselSwipeAreaStyle, getCarouselProps, getCarouselStyles } from './CarouselStyles';
import { CarouselButton } from './components/carousel-button';
import { DotIndicator } from './components/dot-indicator';
import { useCarousel } from './components/use-carousel';
export var Carousel = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var propsWithState = getCarouselProps(props, stateContext);
  var _a = useCarousel(propsWithState),
    containerStyle = _a.containerStyle,
    innerContainerStyle = _a.innerContainerStyle,
    swipeAreaStyle = _a.swipeAreaStyle,
    data = _a.data,
    slideWrapperStyle = _a.slideWrapperStyle,
    renderItem = _a.renderItem,
    showIndicators = _a.showIndicators,
    Indicator = _a.indicator,
    activeIndex = _a.activeIndex,
    animatedXPosition = _a.animatedXPosition,
    childLength = _a.childLength,
    autoPlayTransitionSpeed = _a.autoPlayTransitionSpeed,
    autoPlay = _a.autoPlay,
    containerWidth = _a.containerWidth,
    itemSpacing = _a.itemSpacing,
    panResponder = _a.panResponder,
    singleSlideWidth = _a.singleSlideWidth,
    xPosition = _a.xPosition,
    loop = _a.loop,
    isPressable = _a.isPressable,
    goToNext = _a.goToNext,
    goToPrev = _a.goToPrev,
    goToIndex = _a.goToIndex,
    onLayoutContainer = _a.onLayoutContainer,
    _onPress = _a.onPress,
    _a$activeIndicatorCol = _a.activeIndicatorColor,
    activeIndicatorColor = _a$activeIndicatorCol === void 0 ? RED_500 : _a$activeIndicatorCol,
    _a$inActiveBorderColo = _a.inActiveBorderColor,
    inActiveBorderColor = _a$inActiveBorderColo === void 0 ? BLACK_500 : _a$inActiveBorderColo,
    activeBorderColor = _a.activeBorderColor,
    _a$showCarouselButton = _a.showCarouselButton,
    showCarouselButton = _a$showCarouselButton === void 0 ? false : _a$showCarouselButton,
    carouselButtonProps = _a.carouselButtonProps,
    _a$showNavigationCoun = _a.showNavigationCounterWithButton,
    showNavigationCounterWithButton = _a$showNavigationCoun === void 0 ? false : _a$showNavigationCoun,
    _a$showNavigationCoun2 = _a.showNavigationCounter,
    showNavigationCounter = _a$showNavigationCoun2 === void 0 ? false : _a$showNavigationCoun2,
    _a$inViewTriggerOnce = _a.inViewTriggerOnce,
    inViewTriggerOnce = _a$inViewTriggerOnce === void 0 ? true : _a$inViewTriggerOnce,
    onInViewChange = _a.onInViewChange,
    inViewStyle = _a.inViewStyle,
    _a$testID = _a.testID,
    testID = _a$testID === void 0 ? 'Carousel' : _a$testID,
    restProps = __rest(_a, ["containerStyle", "innerContainerStyle", "swipeAreaStyle", "data", "slideWrapperStyle", "renderItem", "showIndicators", "indicator", "activeIndex", "animatedXPosition", "childLength", "autoPlayTransitionSpeed", "autoPlay", "containerWidth", "itemSpacing", "panResponder", "singleSlideWidth", "xPosition", "loop", "isPressable", "goToNext", "goToPrev", "goToIndex", "onLayoutContainer", "onPress", "activeIndicatorColor", "inActiveBorderColor", "activeBorderColor", "showCarouselButton", "carouselButtonProps", "showNavigationCounterWithButton", "showNavigationCounter", "inViewTriggerOnce", "onInViewChange", "inViewStyle", "testID"]);
  var _ref = carouselButtonProps || {},
    onLeftCarouselButtonPress = _ref.onLeftCarouselButtonPress,
    onRightCarouselButtonPress = _ref.onRightCarouselButtonPress,
    rightCarouselButtonProps = _ref.rightCarouselButtonProps,
    leftCarouselButtonProps = _ref.leftCarouselButtonProps;
  var carouselStyles = getCarouselStyles(propsWithState);
  var CarouselIndicator = Indicator || DotIndicator;
  useImperativeHandle(ref, () => {
    return {
      activeIndex,
      childLength,
      goToNext,
      goToPrev,
      goToIndex
    };
  });
  var CarouselComponent = _jsxs(View, Object.assign({
    style: [carouselStyles.outerContainer, containerStyle],
    onLayout: onLayoutContainer
  }, restProps, getTestId(testID), {
    children: [_jsx(View, Object.assign({
      style: [carouselStyles.container, getAnimatedCarouselContainerStyle(xPosition, containerWidth), innerContainerStyle]
    }, {
      children: _jsx(Animated.View, Object.assign({
        style: [carouselStyles.swipeArea, getAnimatedCarouselSwipeAreaStyle(animatedXPosition), swipeAreaStyle]
      }, (data === null || data === void 0 ? void 0 : data.length) > 1 && Object.assign({}, panResponder.panHandlers), {
        children: data === null || data === void 0 ? void 0 : data.map((item, index) => {
          var Container = isPressable ? Pressable : View;
          return _jsxs(Fragment, {
            children: [_jsx(Container, Object.assign({
              style: [{
                width: singleSlideWidth
              }, slideWrapperStyle]
            }, isPressable ? {
              onPress: () => {
                _onPress === null || _onPress === void 0 ? void 0 : _onPress(item, index);
              }
            } : {}, getTestId(testID, 'container'), {
              children: _jsx(InView, Object.assign({
                triggerOnce: inViewTriggerOnce,
                onChange: inView => {
                  if (inView) {
                    onInViewChange === null || onInViewChange === void 0 ? void 0 : onInViewChange({
                      index,
                      item,
                      inView
                    });
                  }
                },
                style: inViewStyle
              }, {
                children: renderItem(item, index)
              }))
            })), !isLastItem(data, index) && _jsx(View, {
              style: {
                width: itemSpacing
              }
            })]
          }, "carousel_slide_" + index);
        })
      }))
    })), showIndicators && _jsx(View, Object.assign({
      style: carouselStyles.indicatorContainer
    }, {
      children: _jsx(CarouselIndicator, Object.assign({
        count: childLength,
        activeIndex: activeIndex,
        goToIndex: goToIndex,
        autoPlayTransitionSpeed: autoPlayTransitionSpeed,
        autoPlay: autoPlay,
        activeIndicatorColor: activeIndicatorColor,
        inActiveBorderColor: inActiveBorderColor,
        activeBorderColor: activeBorderColor
      }, getTestId(testID, 'indicator')))
    }))]
  }));
  var CarouselWithButton = _jsxs(View, {
    children: [_jsxs(View, Object.assign({
      style: carouselStyles.carouselButtonContainer
    }, {
      children: [_jsx(CarouselButton, Object.assign({
        iconName: "LeftArrowButtonIcon",
        size: "sm",
        disabled: activeIndex === 0
      }, leftCarouselButtonProps, {
        onPress: () => {
          goToPrev();
          onLeftCarouselButtonPress === null || onLeftCarouselButtonPress === void 0 ? void 0 : onLeftCarouselButtonPress();
        }
      })), _jsx(View, Object.assign({
        style: carouselStyles.innerContainer
      }, {
        children: CarouselComponent
      })), _jsx(CarouselButton, Object.assign({
        iconName: "RightArrowButtonIcon",
        size: "sm",
        disabled: !loop && activeIndex === childLength - 1
      }, rightCarouselButtonProps, {
        onPress: () => {
          goToNext();
          onRightCarouselButtonPress === null || onRightCarouselButtonPress === void 0 ? void 0 : onRightCarouselButtonPress();
        }
      }))]
    })), showNavigationCounter && _jsx(View, Object.assign({
      style: carouselStyles.navigationSlideCounter
    }, {
      children: _jsx(Typography, {
        color: BLACK_900,
        multiText: [{
          text: "" + (activeIndex + 1),
          type: 'body-para-md-bold',
          style: {
            textAlign: 'center'
          }
        }, {
          text: "/" + childLength,
          type: 'body-para-md',
          style: {
            textAlign: 'center'
          }
        }]
      })
    }))]
  });
  if (showCarouselButton) {
    return _jsx(_Fragment, {
      children: CarouselWithButton
    });
  }
  if (showNavigationCounterWithButton) {
    return _jsxs(_Fragment, {
      children: [CarouselComponent, _jsxs(View, Object.assign({
        style: carouselStyles.navigationWithButtonContainer
      }, {
        children: [_jsx(CarouselButton, Object.assign({
          iconName: "LeftArrowButtonIcon",
          size: "sm"
        }, leftCarouselButtonProps, {
          disabled: activeIndex === 0,
          onPress: () => {
            goToPrev();
            onLeftCarouselButtonPress === null || onLeftCarouselButtonPress === void 0 ? void 0 : onLeftCarouselButtonPress();
          }
        })), _jsx(Typography, {
          style: carouselStyles.counter,
          type: "body-para-md",
          text: activeIndex + 1 + "/" + childLength
        }), _jsx(CarouselButton, Object.assign({
          iconName: "RightArrowButtonIcon",
          size: "sm"
        }, rightCarouselButtonProps, {
          disabled: !loop && activeIndex === childLength - 1,
          onPress: () => {
            goToNext();
            onRightCarouselButtonPress === null || onRightCarouselButtonPress === void 0 ? void 0 : onRightCarouselButtonPress();
          }
        }))]
      }))]
    });
  }
  return _jsx(_Fragment, {
    children: CarouselComponent
  });
});